import React, { useState } from "react";
import { User, MessageSquare, BookOpen, Zap, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { agentBranding } from "../config/agentBranding";

interface AccountCreationPromptProps {
  onDismiss?: () => void;
  variant?: 'compact' | 'full';
}

export const AccountCreationPrompt: React.FC<AccountCreationPromptProps> = ({ 
  onDismiss,
  variant = 'full' 
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) {return null;}

  const benefits = [
    {
      icon: <MessageSquare className="h-4 w-4" />,
      title: "Persistent Conversations",
      description: agentBranding.accountPrompt.benefitConversations
    },
    {
      icon: <BookOpen className="h-4 w-4" />,
      title: "Todo Lists & Action Items",
      description: agentBranding.accountPrompt.benefitTodoLists
    },
    {
      icon: <Zap className="h-4 w-4" />,
      title: "Advanced AI Features",
      description: agentBranding.accountPrompt.benefitAdvanced
    }
  ];

  if (variant === 'compact') {
    return (
      <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-primary/5 dark:from-blue-900/20 dark:to-primary/10 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {agentBranding.accountPrompt.compactLabel}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="text-xs bg-blue-500 hover:bg-blue-600">
              Sign Up Free
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="mt-4 p-4 bg-gradient-to-br from-blue-50 via-primary/5 to-pink-50 dark:from-blue-900/20 dark:via-primary/10 dark:to-pink-900/20 border-blue-200 dark:border-blue-800">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
            <User className="h-4 w-4 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">
              {agentBranding.accountPrompt.unlockTitle}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {agentBranding.accountPrompt.unlockSubtitle}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDismiss}
          className="h-6 w-6 p-0 text-blue-400 hover:text-blue-600 dark:text-blue-300 dark:hover:text-blue-100"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      {/* Benefits grid */}
      <div className="grid grid-cols-1 gap-3 mb-4">
        {benefits.map((benefit, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className="mt-1 text-blue-500 dark:text-blue-400">
              {benefit.icon}
            </div>
            <div>
              <h5 className="font-medium text-sm text-blue-900 dark:text-blue-100">
                {benefit.title}
              </h5>
              <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 pt-2">
        <Button 
          size="sm" 
          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
          onClick={() => {
            // Navigate to signup
            window.location.href = '/signup?source=cortex_chat';
          }}
        >
          Create Free Account
        </Button>
        <Button 
          size="sm" 
          variant="outline"
          className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-900/20"
          onClick={() => {
            // Navigate to login
            window.location.href = '/login?source=cortex_chat';
          }}
        >
          Sign In
        </Button>
      </div>

      {/* Trust indicators */}
      <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
        <div className="flex items-center justify-center gap-4 text-xs text-blue-600 dark:text-blue-400">
          <span className="flex items-center gap-1">
            ✅ Free forever
          </span>
          <span className="flex items-center gap-1">
            🔒 Secure & private
          </span>
          <span className="flex items-center gap-1">
            ⚡ Instant setup
          </span>
        </div>
      </div>
    </Card>
  );
};
